import { api } from "@/api/client";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";

// Read-only view of the persisted weekly aggregates written by the task #27
// Cloud Run Job (k≥10-suppressed, one doc per completed ISO week). Unlike the
// on-the-fly panels there is no window selector — the window is baked into each
// aggregate. Until the job has run for a completed week, `available` is false
// and we show an empty-state hint rather than blank tables.
export function AggregatesView() {
  const { data, loading, error, lastFetched, refetch } = usePoll((s) => api.aggregates(s), []);

  const demand = data?.demand ?? null;
  const gaps = data?.content_gaps ?? null;
  const pivot = data?.pivot_map ?? null;

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness lastFetched={lastFetched} loading={loading} error={error} onRefresh={refetch} />
      </div>

      <section class="card" style="padding-bottom: var(--space-3);">
        <h2>
          {t("aggregates.title")} <InfoDot k="k10" />
        </h2>
        <PanelHelp k="aggregates.overview" />
      </section>

      {data && !data.available && (
        <section class="card">
          <h2>{t("aggregates.noneTitle")}</h2>
          <p class="muted">{t("aggregates.noneText")}</p>
        </section>
      )}

      {demand && (
        <section class="card">
          <h2>
            {t("aggregates.demand")} <span class="muted">— {demand.window_label}</span>
          </h2>
          <PanelHelp k="aggregates.demand" />
          <p class="muted">
            {t("aggregates.demandMeta", {
              turns: demand.source_turn_count,
              suppressed: demand.suppressed_cell_count,
              k: demand.k_threshold,
            })}
          </p>
          <table class="table">
            <thead>
              <tr>
                <th>{t("aggregates.berufsfeld")}</th>
                <th>{t("aggregates.locale")}</th>
                <th style="text-align: right;">{t("aggregates.turns")}</th>
              </tr>
            </thead>
            <tbody>
              {demand.cells.length === 0 ? (
                <tr>
                  <td colSpan={3} class="muted">
                    {t("aggregates.noCell", { k: demand.k_threshold })}
                  </td>
                </tr>
              ) : (
                demand.cells.map((c) => (
                  <tr key={`${c.berufsfeld}-${c.locale}`}>
                    <td>
                      {c.berufsfeld || <span class="muted">{t("aggregates.unknown")}</span>}
                    </td>
                    <td>{c.locale}</td>
                    <td style="text-align: right;">{c.turn_count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}

      {gaps && (
        <section class="card">
          <h2>
            {t("aggregates.contentGaps")} <span class="muted">— {gaps.window_label}</span>
          </h2>
          <PanelHelp k="aggregates.contentGaps" />
          <p class="muted">
            {t("aggregates.gapsMeta", {
              floor: gaps.relevance_floor,
              suppressed: gaps.suppressed_cell_count,
              k: gaps.k_threshold,
            })}
          </p>
          <table class="table">
            <thead>
              <tr>
                <th>{t("aggregates.berufsfeld")}</th>
                <th>{t("aggregates.locale")}</th>
                <th style="text-align: right;">{t("aggregates.noAnswer")}</th>
                <th style="text-align: right;">{t("aggregates.lowRelevance")}</th>
              </tr>
            </thead>
            <tbody>
              {gaps.cells.length === 0 ? (
                <tr>
                  <td colSpan={4} class="muted">
                    {t("aggregates.noGapCell", { k: gaps.k_threshold })}
                  </td>
                </tr>
              ) : (
                gaps.cells.map((c) => (
                  <tr key={`${c.berufsfeld}-${c.locale}`}>
                    <td>
                      {c.berufsfeld || <span class="muted">{t("aggregates.unknown")}</span>}
                    </td>
                    <td>{c.locale}</td>
                    <td style="text-align: right;">{c.no_answer_count}</td>
                    <td style="text-align: right;">{c.low_relevance_count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}

      {pivot && (
        <section class="card">
          <h2>
            {t("aggregates.pivot")} <span class="muted">— {pivot.window_label}</span>
          </h2>
          <PanelHelp k="aggregates.pivot" />
          <p class="muted">
            {t("aggregates.pivotMeta", {
              sessions: pivot.source_session_count,
              suppressed: pivot.suppressed_cell_count,
              k: pivot.k_threshold,
            })}
          </p>
          <table class="table">
            <thead>
              <tr>
                <th>{t("aggregates.from")}</th>
                <th>{t("aggregates.to")}</th>
                <th style="text-align: right;">{t("common.sessions")}</th>
              </tr>
            </thead>
            <tbody>
              {pivot.cells.length === 0 ? (
                <tr>
                  <td colSpan={3} class="muted">
                    {t("aggregates.noPivot", { k: pivot.k_threshold })}
                  </td>
                </tr>
              ) : (
                pivot.cells.map((c) => (
                  <tr key={`${c.from_berufsfeld}-${c.to_berufsfeld}`}>
                    <td>{c.from_berufsfeld}</td>
                    <td>{c.to_berufsfeld}</td>
                    <td style="text-align: right;">{c.pivot_count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
